import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Plus, Search, Edit, Trash2, Loader2, Tag } from 'lucide-react'
import { apiGet } from '../lib/api'

interface Marca {
  id: string
  nombre: string
  slug: string
  activa: boolean
  productCount: number
}

interface MarcasData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: Marca[]
}

export function Marcas() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isLoading, isFetching } = useQuery<MarcasData>({
    queryKey: ['marcas', page, searchTerm],
    queryFn: () =>
      apiGet(`/api/admin/marcas?page=${page}${searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ''}`),
    placeholderData: (prev) => prev,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/admin/marcas/${id}`, { method: 'DELETE' })
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Error' }))
        throw new Error(err.error || 'Error eliminando')
      }
      return r.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marcas'] })
    },
    onError: (e: Error) => alert(e.message),
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchTerm(q.trim())
  }

  function onDelete(id: string, nombre: string) {
    if (confirm(`¿Eliminar la marca "${nombre}"?`)) {
      deleteMutation.mutate(id)
    }
  }

  return (
    <>
      <Topbar
        title="Marcas"
        subtitle={
          data ? `${data.total.toLocaleString('es-MX')} marcas de repuesto` : 'Cargando…'
        }
        actions={
          <button
            onClick={() => navigate({ to: '/marcas/nueva' as any })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nueva
          </button>
        }
      />
      <div className="p-6">
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar marca por nombre…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando marcas…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Marca</th>
                      <th className="px-4 py-3 font-medium">Slug</th>
                      <th className="px-4 py-3 font-medium text-right">Productos</th>
                      <th className="w-20 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-12 text-center text-ink-500">
                          No hay marcas para mostrar
                        </td>
                      </tr>
                    )}
                    {data.data.map((m) => (
                      <tr
                        key={m.id}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600">
                              <Tag className="size-4" />
                            </div>
                            <div className="font-medium text-ink-900">{m.nombre}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-600">{m.slug}</td>
                        <td className="px-4 py-3 text-right">
                          {m.productCount > 0 ? (
                            <span className="font-semibold text-ink-900">
                              {m.productCount.toLocaleString('es-MX')}
                            </span>
                          ) : (
                            <span className="text-ink-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <button
                              onClick={() => navigate({ to: `/marcas/${m.id}` as any })}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                              title="Editar"
                            >
                              <Edit className="size-4" />
                            </button>
                            <button
                              onClick={() => onDelete(m.id, m.nombre)}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700"
                              title="Eliminar"
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong> (
                  {data.total.toLocaleString('es-MX')} marcas)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
